'use client';

import { Order, OrderItem } from '@instamenu/data-access';
import { dateFormats, formatCurrency, formatDate } from '@instamenu/formatters';
import { Box, Button, Card, Flex, ModalNext, Stack, Text } from '@instamenu/ui-system-next';
import { palette, themeVars } from '@instamenu/ui-vanilla-extract';
import { EmptyImg } from '@instamenu/ui-icons';
import { useRouter } from 'next/navigation';
import { imgStyles } from './order-history.css';
import Image from 'next/image';
import { useState } from 'react';
import { DeliveryTracker } from '../delivery-tracker/delivery-tracker';
import {
  deliveryStatusMap,
  getCustomerFacingDeliveryStatus,
  getStatusBackgroundColor,
} from '../delivery-tracker/status-map';
import { makePath } from '../utils';

const toLowerCaseExceptFirst = (str: string) => str[0] + str.slice(1).toLowerCase();

export const OrderHistoryCard = ({ order }: { order: Order }) => {
  const [isOpen, setIsOpen] = useState(false);
  const router = useRouter();

  const itemQuantity = order.orderItems.reduce(
    (total: number, item: OrderItem) => total + item.quantity,
    0,
  );

  const getDisplayStatus = () => {
    const { delivery, status } = order;

    if (delivery?.status) {
      const statusInfo = deliveryStatusMap(
        status,
        getCustomerFacingDeliveryStatus(delivery.status),
      );
      return statusInfo.label;
    }

    return toLowerCaseExceptFirst(status);
  };

  const getStatusColor = () => getStatusBackgroundColor(order.delivery?.status ?? order.status);

  return (
    <Card
      css={{
        padding: 16,
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
      key={order.id}
    >
      {order.restaurant.heroImageUrl ? (
        <Image
          src={order.restaurant.heroImageUrl}
          alt={order.restaurant.name ?? ''}
          className={imgStyles}
          width={90}
          height={60}
        />
      ) : (
        <EmptyImg className={imgStyles} />
      )}
      <Stack style={{ marginLeft: 16, flex: 1, gap: 4 }}>
        <Flex style={{ alignItems: 'baseline', gap: 8 }}>
          <Text maxLines={1} variant="subtitle2" css={{ display: 'inline' }}>
            {order.restaurant.name}
          </Text>
        </Flex>
        <Text variant="caption2">
          {order.placedAt && formatDate(order.placedAt, dateFormats.dateTimeShort)}
        </Text>

        <Flex style={{ alignItems: 'center', gap: 8 }}>
          <Text variant="caption2">Items: {itemQuantity}</Text>
          <Text variant="caption2">•</Text>
          <Text variant="caption2">{formatCurrency(order.total)}</Text>
          {order.refund && (
            <Text variant="caption2" css={{ color: palette.error }}>
              Refunded
            </Text>
          )}
        </Flex>
        <Flex style={{ alignItems: 'center', gap: 8 }}>
          <Box
            css={{
              padding: '2px 4px',
              width: 'fit-content',
              borderRadius: '4px',
              border: `2px solid ${getStatusColor()}`,
              cursor: order.fulfillment === 'DELIVERY' ? 'pointer' : 'default',
            }}
            onClick={() => order.fulfillment === 'DELIVERY' && setIsOpen(true)}
          >
            <Text variant="caption2">{getDisplayStatus()}</Text>
          </Box>
          <ModalNext
            opened={isOpen}
            containerStyle={{ padding: themeVars.pad[12] }}
            onRequestClose={() => setIsOpen(false)}
          >
            <DeliveryTracker hasDeliveryInfo order={order} />
          </ModalNext>
        </Flex>
      </Stack>
      <Stack style={{ gap: 8 }}>
        <Button
          size="xs"
          onClick={() => {
            router.push(makePath({ restaurantId: order.restaurant.id }));
          }}
          css={{ fontSize: 12 }}
          variant="secondary"
        >
          Menu
        </Button>
        <Button
          onClick={() => router.push(`${process.env.NEXT_PUBLIC_API_ENDPOINT}/orders/${order.id}`)}
          size="xs"
          css={{ fontSize: 12 }}
        >
          View order
        </Button>
      </Stack>
    </Card>
  );
};
